import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ButtonAddNewComponent } from '../../../../../shared/components/button-add-new/button-add-new.component';
import { ExpansionPanel } from '../../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { ProductionCreateRequest } from '../../../../référentiel-métier-ressources-professionnelles/requests/production-create-request.model';
import { ProductionStore } from '../../../../référentiel-métier-ressources-professionnelles/store/production.store';
import { AptitudeStore } from '../../../processus-aptitude/store/aptitude.store';

@Component({
  selector: 'app-productions-form',
  imports: [
    ExpansionPanel,
    ReactiveFormsModule,
    ButtonAddNewComponent,
    HlmFieldImports,
    HlmInputImports,
    HlmSelectImports,
    BrnSelectImports,
    LabelRequiredDirective,
  ],
  templateUrl: './productions-form.component.html',
})
export class ProductionsFormComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #productionStore = inject(ProductionStore);
  readonly #aptitudeStore = inject(AptitudeStore);

  readonly idAptitude = input<string>();

  readonly aptitudes = linkedSignal(() => this.#aptitudeStore.entities());

  readonly form = this.#fb.nonNullable.group({
    aptitude: [null, [Validators.required]],
    productions: this.#fb.array([]),
  });

  constructor() {
    effect(() => this.aptitude.setValue(this.idAptitude()));
  }

  ngOnInit(): void {
    this.addProduction();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addProduction() {
    const production = this.#fb.nonNullable.group({
      nom: [null, [Validators.required]],
    });
    this.productions.push(production);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouvellesProductions: ProductionCreateRequest = {
      idAptitude: this.aptitude.value,
      productions: this.productions.value.filter((x: { nom: string }) => !!x.nom?.trim()),
    };

    if (!nouvellesProductions.productions || nouvellesProductions.productions.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#productionStore.créer({
      data: nouvellesProductions,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des productions réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouvellesProductions.productions),
    });
  }

  alreadyExists(
    nouvellesProductions: {
      nom: string;
    }[],
  ) {
    const productions = this.aptitudes().flatMap((x) => x.productions!);

    let doublonsAvecProductionsExistants = productions.filter((x) =>
      nouvellesProductions.some((y) => x.idAptitude === this.aptitude.value && y.nom === x.nom),
    );
    this.productions.controls.forEach((control) => {
      if (
        doublonsAvecProductionsExistants.some((x) => x.nom === control.value.nom) ||
        this.productions.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });

    this.productions.markAllAsTouched();
  }

  get aptitude() {
    return this.form.get('aptitude') as AbstractControl;
  }

  get productions() {
    return this.form.get('productions') as FormArray;
  }
}
